import React from 'react';
import ReactSelect5, { Option } from '@/components/common/ReactSelect5';
import cn from '@/utils/cn';
import { StateOption } from './types';

const GRADE_OPTIONS: Option[] = [
  { label: 'All grades', value: '' },
  { label: 'K', value: 'K' },
  ...Array.from({ length: 12 }, (_, i) => ({
    label: String(i + 1),
    value: String(i + 1),
  })),
];

interface StandardsToolbarProps {
  grade: string;
  hasData: boolean;
  isExpanded: boolean;
  matchCount?: number;
  searchQuery: string;
  stateId: number | null;
  stateOptions: StateOption[];
  onExpandToggle: () => void;
  onGradeChange: (grade: string) => void;
  onSearchChange: (query: string) => void;
  onStateChange: (stateId: number | null) => void;
}

const StandardsToolbar = ({
  grade,
  hasData,
  isExpanded,
  matchCount,
  searchQuery,
  stateId,
  stateOptions,
  onExpandToggle,
  onGradeChange,
  onSearchChange,
  onStateChange,
}: StandardsToolbarProps) => {
  const selectedState = stateOptions.find(o => o.value === String(stateId)) ?? null;
  const selectedGrade = GRADE_OPTIONS.find(o => o.value === grade) ?? GRADE_OPTIONS[0];

  return (
    <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap tw-bg-light-grey tw-p-3 tw-rounded-t-lg">
      <div className="tw-w-56 tw-bg-white tw-rounded">
        <ReactSelect5
          isClearable
          menuPortalTarget={document.body}
          options={stateOptions}
          placeholder="Select State"
          value={selectedState}
          onChange={option => onStateChange(option ? Number((option as Option).value) : null)}
        />
      </div>

      <div className="tw-w-36 tw-bg-white tw-rounded">
        <ReactSelect5
          isDisabled={stateId === null}
          menuPortalTarget={document.body}
          options={GRADE_OPTIONS}
          value={selectedGrade}
          onChange={option => onGradeChange((option as Option)?.value ?? '')}
        />
      </div>

      <button
        className={cn(
          'btn btn--outline-purple tw-px-3 tw-text-sm !tw-bg-white',
          'hover:!tw-bg-white hover:!tw-text-tci-purple',
        )}
        disabled={!hasData}
        type="button"
        onClick={onExpandToggle}
      >
        {isExpanded ? 'Collapse All' : 'Expand All'}
      </button>

      <div className="tw-ml-auto tw-min-w-0 tw-w-72 tw-flex-shrink tw-self-stretch tw-relative">
        <input
          className={cn(
            'tw-w-full tw-h-full tw-px-3 tw-text-sm tw-rounded tw-border tw-border-solid',
            'tw-border-grey tw-outline-none tw-box-border tw-bg-white',
            'focus:tw-border-tci-purple focus:tw-shadow-[0_0_0_1px_#522e91]',
            searchQuery && 'tw-pr-20',
          )}
          placeholder="Search by alias, description, or ID…"
          type="text"
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
        />
        {searchQuery && (
          <div className="tw-absolute tw-right-1 tw-top-1/2 tw--translate-y-1/2 tw-flex tw-items-center tw-gap-1">
            {matchCount !== undefined && (
              <span className="tw-text-xs tw-text-dark-grey">
                {matchCount} found
              </span>
            )}
            <button
              aria-label="Clear search"
              className="tw-bg-transparent tw-border-0 tw-cursor-pointer tw-text-dark-grey tw-text-sm tw-px-1"
              type="button"
              onClick={() => onSearchChange('')}
            >
              &times;
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardsToolbar;
